'use client';
import {
  deleteFollowEvent,
  postFollowEvent,
} from '@requests/get-pages-content/favorite-events';
import { v2Links } from '@utils/navigation/links';
import { getLeagueTeamPath } from '@utils/navigation/league-public-paths';
import kebabCase from '@utils/kebab';
import { TeamProps } from '@v2/types';
import { AlertToast } from '@v2/ui/toast/toast';
import { AlertTriangle } from '@v2/ui/Icon/Alert';
import { Star01 } from '@v2/ui/Icon/Shapes';
import { Spinner } from '@v2/ui/Icon/Spinner/Spinner';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import toast from 'react-hot-toast';

export const TeamItem = ({
  team,
  leagueSlug,
  displayName,
  isFavorite,
  userToken,
  onFavoriteUpdate,
  onRemoveFavoriteUpdate,
}) => {
  const starButtonClasses = clsx('cursor-pointer ml-3 group-hover:visible', {
    'visible md:invisible': !isFavorite,
    visible: isFavorite,
  });

  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const onFavoriteClick = async (team: TeamProps) => {
    if (!userToken) {
      return router.push(v2Links.login);
    }

    if (isFavorite) {
      return onUnfavoriteClick(team);
    }

    const { teamId, team: name } = team;
    const type = 'performer';
    setIsLoading(true);

    const following = await postFollowEvent(
      Number(teamId),
      'performer',
      name,
      userToken
    );

    if (!following.error) {
      onFavoriteUpdate({ id: teamId, type: type.slice(0, 1), name });

      setIsLoading(false);
    } else {
      toast.custom(
        (t) => (
          <AlertToast
            {...t}
            title="Oops, something went wrong"
            content={'Please try again in a minute.'}
            icon={<AlertTriangle />}
          />
        ),
        { position: 'top-right' }
      );
    }

    setIsLoading(false);
  };

  const onUnfavoriteClick = async (team: TeamProps) => {
    setIsLoading(true);

    const { teamId, team: name } = team;

    const unfollow = await deleteFollowEvent(teamId, 'performer', userToken);

    if (!unfollow.error) {
      onRemoveFavoriteUpdate({
        id: teamId,
        type: 'performer',
        name: name,
      });
      setIsLoading(false);
    } else {
      toast.custom(
        (t) => (
          <AlertToast
            {...t}
            title="Oops, something went wrong"
            content={'Please try again in a minute.'}
            icon={<AlertTriangle />}
          />
        ),
        { position: 'top-right' }
      );
    }

    setIsLoading(false);
  };

  const getUrl = (team: {
    team: string;
    league: string;
    slug?: string;
    canonicalSlug?: string;
  }) => {
    const urlSlug = leagueSlug?.toLowerCase();

    if (team.canonicalSlug) {
      return getLeagueTeamPath(urlSlug, team.canonicalSlug);
    }

    if (team.team === 'San Francisco 49ers') {
      return getLeagueTeamPath(urlSlug, 'san-francisco-49ers');
    }
    if (team.team === 'Philadelphia 76ers') {
      return getLeagueTeamPath(urlSlug, 'philadelphia-76ers');
    }

    if (urlSlug === 'mlb' && team.team === 'Oakland Athletics') {
      return getLeagueTeamPath(urlSlug, 'athletics');
    }

    return getLeagueTeamPath(
      urlSlug,
      team.canonicalSlug ??
        team.slug ??
        kebabCase(team.team.replace(/&/g, 'and'))
    );
  };

  const canFavorite = Boolean(team?.teamId);

  return (
    <li className="group relative mb-2 flex">
      <Link
        prefetch={false}
        href={getUrl(team)}
        className="text-sm font-semibold text-gray-800"
      >
        {displayName ?? team.team}
      </Link>
      {canFavorite && isLoading ? (
        <Spinner className="ml-3 mt-1" size="12" />
      ) : canFavorite ? (
        <button
          onClick={() => {
            onFavoriteClick(team);
          }}
          className={starButtonClasses}
        >
          <Star01
            size="16"
            fill={isFavorite ? '#667085' : ''}
            pathProps={{ stroke: '#667085' }}
          />
        </button>
      ) : null}
    </li>
  );
};
